/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, useContext, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { useRouter } from 'next/router';
import { settlementCurrenyDetails } from '../../../modules/graphql/index';
import { PageParamsContext } from '../../../modules/context/PageContext';
import { INTERNETBAR_PRICING_TIER_OPTIONS, INTERNETBAR_PRICING_ZONE_OPTIONS } from '../utils/constant';
import { EnrollmentSourceCodeMapping, InternetBarDataProps } from '../InternetBar.types';
import { settlementCurrenyDetailsMock } from '../__mock__/InternetBar.model';
import { getRegionEnrollmentSourceCode, settlementCurrencyHandler } from '../utils/handler';

export const InternetPricingData: FC<InternetBarDataProps> = (pageProps: InternetBarDataProps) => {
  const { preProcessorResponse, lang, sessionID, requestId } = useContext(PageParamsContext);

  const router = useRouter();
  const urlQueryParams: Record<string, any> = router.query;

  const isServer = !(typeof window != 'undefined' && window.document);
  let { data, loading, error } = useQuery(settlementCurrenyDetails, {
    fetchPolicy: isServer ? 'network-only' : 'cache-first',
    variables: {
      propertyId: preProcessorResponse?.marsha ?? '',
      version: 'V2',
      connectionPricingFilter: {
        pricingType: [INTERNETBAR_PRICING_ZONE_OPTIONS.guest, INTERNETBAR_PRICING_ZONE_OPTIONS.public],
        tierType: [INTERNETBAR_PRICING_TIER_OPTIONS.tierOne, INTERNETBAR_PRICING_TIER_OPTIONS.tierTwo],
      },
    },
    context: {
      headers: {
        'accept-language': lang ?? 'en-US',
        'x-b3-traceId': sessionID ?? 'fallback-token',
        'x-b3-spanId': requestId !== '' ? requestId : `${Date.now()}`,
        'correlation-id': sessionID ?? 'fallback-token',
      },
    },
    skip: pageProps?.isAuthorMode,
  });

  if (pageProps?.isAuthorMode) {
    loading = false;
    data = { ...settlementCurrenyDetailsMock };
    error = undefined;
  }

  useEffect(() => {
    const { isAuthorMode, dataLists } = pageProps;
    if (!isAuthorMode) {
      let internetPricingData = {};
      const {
        unknownFreeConnection = false as boolean,
        unknownFreeConnectionForPostConnect = false as boolean,
        enrollmentSourceCodeMapping = {} as EnrollmentSourceCodeMapping,
      } = preProcessorResponse || {};

      if (!loading) {
        if (data && !error) {
          let Z = '';
          const zList = (dataLists?.zParamList?.toUpperCase() ?? '').split(',');

          if (urlQueryParams['Z'] !== null && zList.includes((urlQueryParams['Z'] ?? '').toUpperCase())) {
            Z = (urlQueryParams['Z'] ?? 'GUEST').toUpperCase();
          } else {
            Z = 'GUEST';
          }
          const zoneMapping: Record<string, any> = {
            ...dataLists?.zoneValues,
          };
          const { enrollmentSourceCode, loyaltyEnrollmentSourceCode } = dataLists;
          const propertyTier = zoneMapping?.['GUEST'];
          const value: EnrollmentSourceCodeMapping = enrollmentSourceCodeMapping ?? {};
          const finalEnrollmentSourceCode = getRegionEnrollmentSourceCode(
            value,
            data?.property,
            unknownFreeConnection || unknownFreeConnectionForPostConnect
              ? unknownFreeConnection || unknownFreeConnectionForPostConnect
              : urlQueryParams['ppv5Enrollment']?.toString()?.toLowerCase() === 'true',
            propertyTier,
            loyaltyEnrollmentSourceCode ?? '',
            enrollmentSourceCode ?? ''
          );
          const settlementData = settlementCurrencyHandler({ ...data }, Z, pageProps?.internetPricingModel?.currency);
          internetPricingData = {
            data: {
              enrollmentSourceCode: finalEnrollmentSourceCode,
              settlementCurrencyData: settlementData,
              uxlGlobalDivision: data?.property?.basicInformation?.globalDivision,
              uxlCountryCode: data?.property?.contactInformation?.address?.country?.code,
            },
            loading: loading,
            error: null,
          };
        } else if (error) {
          internetPricingData = {
            data: null,
            loading: loading,
            error: error,
          };
        }
        pageProps?.handleInternetPricingData(internetPricingData);
      }
    }
  }, [data, loading, error]);

  return null;
};
